import { tr, lazyList } from "./i18n.js";
/**
 * Digital editions form helpers (2026-10-19). Mirrors the title logic
 * in backend/editions.py so the title follows the date as the editor
 * picks it, until they type their own.
 *
 * Pure and framework-free — node --test frontend/scripts/editionForm.test.mjs
 */

const MONTHS = lazyList(() => [
  tr("January"), tr("February"), tr("March"), tr("April"), "May", tr("June"), tr("July"),
  tr("August"), tr("September"), tr("October"), tr("November"), tr("December"),
]);

/** "2026-10-05" → "October 5, 2026"; "" when the date is unusable. */
export function longDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  if (!m) return "";
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return "";
  return `${MONTHS[month - 1]} ${day}, ${m[1]}`;
}

/** Replace {date}, {site} and {issue}; other braces stay as typed. */
export function fillTemplate(template, { date, site = "", issue = "" }) {
  return String(template || "")
    .split("{date}").join(longDate(date))
    .split("{site}").join(site || "")
    .split("{issue}").join(issue || "")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

/**
 * The title to show after the date or issue changes. While the title is
 * still the automatic one for the OLD values it follows the new ones;
 * once the editor has typed their own it is left alone.
 */
export function followTitle({ title, template, site, before, after }) {
  const autoBefore = fillTemplate(template, { site, ...before });
  if (!String(title || "").trim() || title === autoBefore) {
    return fillTemplate(template, { site, ...after });
  }
  return title;
}

/** Which host a link points at, for the "Adobe / Flipsnack" chip. */
export function platformOf(url) {
  let host = "";
  try {
    host = new URL(String(url || "").trim()).hostname.toLowerCase();
  } catch {
    return "";
  }
  if (host === "indd.adobe.com") return "Adobe Publish Online";
  if (host === "flipsnack.com" || host.endsWith(".flipsnack.com")) return "Flipsnack";
  return "";
}

/** Blank edition-settings form state from GET /settings. */
export function settingsForm(settings) {
  const eff = settings?.edition_settings_effective || {};
  return {
    category_ids: Array.isArray(eff.category_ids) ? eff.category_ids : [],
    author_id: eff.author_id ?? null,
    cadence_days: eff.cadence_days ?? 14,
    title_template: eff.title_template ?? "{date} Edition",
    link_text: eff.link_text ?? "",
    text_after: eff.text_after ?? "",
    excerpt_template: eff.excerpt_template ?? "",
    facebook_template: eff.facebook_template ?? "",
    instagram_template: eff.instagram_template ?? "",
  };
}

/** Status pill text for an edition row / header. */
export function statusLabel(status) {
  switch (status) {
    case "published_live":
      return tr("Live");
    case "scheduled":
      return tr("Scheduled");
    case "published_draft":
      return tr("WordPress draft");
    default:
      return tr("Not posted");
  }
}
